import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import useSubscribeService from '../hooks/subscribe-service.hook';
import obelisk from '../clients/obelisk';
import { toast } from 'react-toastify';
import telegram from '../clients/bot';
import PropTypes from 'prop-types';
import configs from '../config/app';

const TelegramSubscribeContext = createContext();

export const TelegramSubscribeProvider = ({ children }) => {
  const [status, setStatus] = useState({ loading: false, ready: false });
  const [userActive, setUserActive] = useState(false);
  const [subscriberId, setSubscriberId] = useState(null);
  const [longPoll, setLongPoll] = useState(false);
  const pollTimeoutRef = useRef(null);
  const pollCountRef = useRef(0);

  const { loading, setSubscriptionName, isExpired } = useSubscribeService();

  const defaultParams = {
    associate_id: subscriberId,
    client_id: configs.clientId,
  };

  // Activate the bot service
  const activateService = async () => {
    try {
      // Fetch our SSO token
      const { data: token } = await obelisk()
        .setHeader('content-type', 'text/plain')
        .get('Authorize/GetSSOToken', { AssociateId: subscriberId });
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      // If we got a token, forward to telegram bot
      if (token) {
        const url = `https://toijtskfvrkzhrgddasi.supabase.co/functions/v1/link-telegram?token=${token}&client_id=${configs.clientId}`;
        // Open the URL in a new tab
        const newWindow = window.open(url, '_blank');
        pollCountRef.current = 0; // Reset poll counter
        setLongPoll(true);
        if (
          isSafari &&
          (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined')
        ) {
          alert(
            'Popup was blocked by your browser. Please allow popups for this site to continue.'
          );
        }
      } else {
        toast.error('There was an issue activating the service. Please try again later.');
      }
    } catch (error) {
      toast.error('There was an issue activating the service. Please try again later.');
      console.error('Error activating service:', error);
    }
  };

  const isActivated = (response) => {
    // See if user is found
    const found = !(response?.data?.reason === 'not_found');
    // See if user is expired
    const active =
      response?.data?.expires_at && new Date(response.data.expires_at) > new Date();
    return found && active;
  };
  /**
   * Fetches the status of the user in the telegram bot service
   */
  const getUserStatus = useCallback(async () => {
    try {
      // If not currently fetching
      if (!status.ready && !status.loading) {
        // Start fetching
        setStatus(() => ({ ready: false, loading: true }));
        const resp = await telegram().get('check-user-status', defaultParams);
        setUserActive(isActivated(resp));
      }
      setStatus((prev) => ({ ...prev, loading: false, ready: true }));
    } catch (error) {
      // If the error is saying not active
      if (error?.response?.data?.reason === 'not_found') {
        // Remove active status if active
        if (userActive) {
          setUserActive(false);
        }
      }
    } finally {
      // Make ready
      setStatus((prev) => ({ ...prev, loading: false, ready: true }));
    }
  }, [status.ready, status.loading, subscriberId, userActive]);

  /**
   * Polls the user status for long polling - always checks regardless of current status
   */
  const pollUserStatus = useCallback(async () => {
    try {
      const resp = await telegram().get('check-user-status', defaultParams);
      setUserActive(isActivated(resp));
      return isActivated(resp);
    } catch (error) {
      return false;
    }
  }, [subscriberId]);

  // Fetch the product status
  useEffect(() => {
    if (!status.ready && !status.loading && subscriberId) {
      getUserStatus();
    }
  }, [status?.ready, status.loading, getUserStatus, subscriberId]);

  // Handle long polling when activated
  useEffect(() => {
    if (longPoll && !userActive) {
      const poll = async () => {
        pollCountRef.current += 1;

        // Check if we've exceeded the maximum number of attempts
        if (pollCountRef.current > 4) {
          toast.error('Service activation timed out. Please try again.');
          setLongPoll(false);
          pollTimeoutRef.current = null;
          return;
        }
        const isActive = await pollUserStatus();
        // If user is still not active and longPoll is still true, schedule next poll
        if (!isActive && longPoll && !userActive && pollCountRef.current < 4) {
          const timeoutId = setTimeout(poll, 10000);
          pollTimeoutRef.current = timeoutId;
        } else if (pollCountRef.current >= 4) {
          // Cancel if we've reached max attempts
          toast.error('Service activation timed out. Please try again.');
          setLongPoll(false);
          pollTimeoutRef.current = null;
        }
      };

      // Start the first poll after 10 seconds
      const timeoutId = setTimeout(poll, 10000);
      pollTimeoutRef.current = timeoutId;

      return () => {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      };
    }
  }, [longPoll, userActive, pollUserStatus]);

  // Cleanup timeout when userActive becomes true
  useEffect(() => {
    if (userActive && pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
      pollCountRef.current = 0; // Reset counter on success
      if (longPoll) {
        toast.success('Service activated successfully!');
        setLongPoll(false);
      }
    }
  }, [userActive, longPoll]);

  return (
    <TelegramSubscribeContext.Provider
      value={{
        setSubscriberId,
        activateService,
        loading,
        setSubscriptionName,
        isExpired,
        subscriberId,
        status,
        userActive,
        longPoll,
        setUserActive,
        setLongPoll,
      }}
    >
      {children}
    </TelegramSubscribeContext.Provider>
  );
};

export const useTelegramSubscribe = () => {
  const context = useContext(TelegramSubscribeContext);
  if (context === undefined) {
    throw new Error(
      'useTelegramSubscribe must be used within a TelegramSubscribeProvider'
    );
  }
  return context;
};

TelegramSubscribeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TelegramSubscribeProvider;
